/**
 * 광고 소재별 랜딩 페이지 설정
 *
 * 디자인 원칙: whatquiz-design 스킬의 토스 10원칙 준수
 * - 이모지 사용 금지, SVG 아이콘만 사용
 * - 강조색 #3182f6 하나만
 * - 해요체, 능동형, 일상어
 * - Value First: 혜택 먼저 -> CTA
 * - One Thing: 한 화면에 하나의 핵심 메시지
 *
 * 시즌 마감/카피는 SEASON_CONFIG에서 동적으로 가져옵니다.
 * 가격은 cutover 시간 분기로 자동 전환 (5/31까지 99k, 6/1부터 140k, 10월 이벤트 중에는 이벤트가).
 * promoCode는 'newyear2026'로 항상 동일.
 */

import { getNewYearPrices, getDiscountRateLabel } from '../../../config/seasonConfig';
import { TEACHER_COUNT_LABEL } from '../../../constants/siteStats';

const COMMON_SOCIAL_PROOF = {
  userCount: TEACHER_COUNT_LABEL,
  badges: ['KERIS 에듀테크 우수기업', '학교장터(S2B) 등록', '세금계산서 발급 가능'],
  testimonials: [
    { name: '김OO 선생님', school: '서울 OO중', text: '생기부 시즌에 처음으로 야근 안 했어요. 30명 3분이면 끝나요.' },
    { name: '박OO 선생님', school: '경기 OO초', text: 'PPT 퀄리티 보고 깜짝 놀랐어요. 미리캔버스보다 빨라요.' },
    { name: '이OO 선생님', school: '부산 OO고', text: '동학년 선생님들 다 같이 쓰고 있어요. 업무가 확 줄었습니다.' },
  ],
};

const COMMON_FEATURES = [
  { key: 'ppt', name: 'AI PPT', time: '3분', desc: '주제만 입력하면 수업 PPT가 완성돼요' },
  { key: 'worksheet', name: 'AI 학습지', time: '3분', desc: '과목별 맞춤 학습지를 자동으로 출제해요' },
  { key: 'image', name: 'AI 이미지', time: '1분', desc: '수업 삽화, 포스터를 바로 만들 수 있어요' },
  { key: 'saenggibu', name: 'AI 생기부', time: '3분', desc: '30명 생기부를 한번에 작성하고 검사해요' },
  { key: 'scoring', name: 'AI 채점', time: '2분', desc: '사진만 찍으면 서술형을 자동으로 채점해요' },
];

// PRICE_PLANS는 cutover 시간 분기로 동적 생성됨 (사용 시점에 getPricePlans() 호출).
// 기본 export 배열은 fallback(신가). 컴포넌트는 getPricePlans()로 시점별 가격을 받는다.
const PRICE_PLANS = [
  { period: '12개월', original: '200,000', sale: '140,000', discount: '30%', monthly: '11,667', highlight: true },
  { period: '6개월', original: '110,000', sale: '82,500', discount: '25%', monthly: '13,750', highlight: false },
  { period: '3개월', original: '60,000', sale: '51,000', discount: '15%', monthly: '17,000', highlight: false },
];

export const getPricePlans = (now = new Date()) => {
  const p = getNewYearPrices(now);
  const fmt = (n) => n.toLocaleString();
  return [
    { period: '12개월', original: '200,000', sale: fmt(p['12month']), discount: getDiscountRateLabel('12month', now), monthly: Math.round(p['12month'] / 12).toLocaleString(), highlight: true },
    { period: '6개월', original: '110,000', sale: fmt(p['6month']), discount: getDiscountRateLabel('6month', now), monthly: Math.round(p['6month'] / 6).toLocaleString(), highlight: false },
    { period: '3개월', original: '60,000', sale: fmt(p['3month']), discount: getDiscountRateLabel('3month', now), monthly: Math.round(p['3month'] / 3).toLocaleString(), highlight: false },
  ];
};

export const AD_LANDING_CONFIG = {

  // ── Group A: 기능 체험형 (Value First -> 바로 사용) ──

  'ppt': {
    adSetName: '3_PPT생성',
    template: 'feature-demo',
    hero: {
      headline: '수업 PPT,',
      highlight: '3분이면 끝나요',
      sub: '주제만 말하면 AI가 수업용 PPT를 완성해요.\nPPTX로 바로 다운로드할 수 있어요.',
      badge: 'AI PPT 자동 생성',
    },
    cta: { text: '무료로 PPT 만들기', dest: '/chat?category=ppt_maker', sub: '가입 없이 바로 체험할 수 있어요' },
    focusFeature: 'ppt',
    showPrice: false,
    demo: 'ppt',
    steps: [
      { label: '주제 입력', desc: '"중2 과학 광합성 15분 수업"' },
      { label: '아웃라인 확인', desc: 'AI가 구성한 슬라이드를 수정해요' },
      { label: 'PPTX 다운로드', desc: '10가지 테마 중 선택하면 완성' },
    ],
  },

  'class-image': {
    adSetName: '2_수업삽화',
    template: 'feature-demo',
    hero: {
      headline: '수업 자료 이미지,',
      highlight: 'AI가 바로 그려줘요',
      sub: '"광합성 과정을 보여주는 그림 만들어줘"\n1분이면 완성돼요. 어떤 과목이든 무료예요.',
      badge: 'AI 수업 이미지',
    },
    cta: { text: '무료로 이미지 만들기', dest: '/chat?category=image_generation', sub: '저작권 걱정 없이 수업에 활용하세요' },
    focusFeature: 'image',
    showPrice: false,
    demo: 'image',
    steps: [
      { label: '설명 입력', desc: '"광합성 과정을 보여주는 그림"' },
      { label: 'AI가 생성', desc: '수업에 딱 맞는 이미지가 완성돼요' },
      { label: '다운로드', desc: 'PPT, 학습지, 시험지에 바로 활용' },
    ],
  },

  'poster': {
    adSetName: '1_학급포스터',
    template: 'feature-demo',
    hero: {
      headline: '학급 포스터,',
      highlight: 'AI가 1분이면 만들어줘요',
      sub: '규칙 포스터, 시간표, 급훈, 당번표, 안내문까지\n말만 하면 AI가 깔끔하게 완성해요.',
      badge: 'AI 포스터 생성',
    },
    cta: { text: '무료로 포스터 만들기', dest: '/chat?category=image_generation', sub: 'A4, A1 인쇄용으로 다운로드할 수 있어요' },
    focusFeature: 'image',
    showPrice: false,
    demo: 'image',
    steps: [
      { label: '포스터 종류 선택', desc: '"우리반 규칙 포스터 만들어줘"' },
      { label: 'AI가 디자인', desc: '깔끔한 레이아웃으로 완성돼요' },
      { label: '인쇄 및 활용', desc: 'A4/A1 사이즈로 바로 인쇄' },
    ],
  },

  'all-in-one': {
    adSetName: 'D_올인원캐러셀',
    template: 'feature-demo',
    hero: {
      headline: '수업 준비,',
      highlight: '밤새울 필요 없어요',
      sub: 'PPT, 학습지, 채점, 생기부, 이미지까지\n선생님의 모든 업무를 12분으로 줄여드려요.',
      badge: '교사 AI 올인원',
    },
    cta: { text: '무료로 시작하기', dest: '/chat', sub: '가입 없이 바로 체험할 수 있어요' },
    focusFeature: null,
    showPrice: false,
    demo: 'class_flow',
    steps: [
      { label: 'PPT 3분', desc: '주제만 입력하면 수업 PPT가 완성돼요' },
      { label: '학습지 3분', desc: '과목별 맞춤 학습지를 자동 출제해요' },
      { label: '생기부 3분', desc: '30명 일괄 작성, 검사까지 한번에' },
    ],
  },

  // ── Group B: 학급사진 변환형 (IKEA Effect -> 바이럴) ──

  'class-photo': {
    adSetName: 'B_우리반학급사진',
    template: 'class-photo',
    hero: {
      headline: '우리반 사진이',
      highlight: '캐릭터 그림이 돼요',
      sub: '학급 단체사진과 원하는 문구를 넣으면\nAI가 문구까지 어우러진 캐릭터 작품으로 만들어드려요.',
      badge: '옛 인기 기능 다시 열었어요',
    },
    cta: {
      text: '우리반 학급사진 만들기',
      dest: '/events/class-photo',
      sub: '둥근 한글 타이포·장식까지 한 번에 디자인해요',
    },
  },

  'single-photo': {
    adSetName: 'B_단일이미지',
    template: 'class-photo',
    hero: {
      headline: '우리반 사진이',
      highlight: 'AI를 만나면?',
      sub: '단체사진 한 장을 올리면\n밝고 따뜻한 캐릭터 그림으로 바꿔드려요.',
      badge: '가입하면 7일 PRO',
    },
    cta: { text: '무료로 만들어보기', dest: '/events/class-photo', sub: '카톡 프사, 교실 게시판에 활용하세요' },
  },

  'emotion': {
    adSetName: 'C_감성캐러셀',
    template: 'class-photo',
    hero: {
      headline: '올해 첫 교실에',
      highlight: '들어서는 순간',
      sub: '새학기 첫날, 우리반 사진으로\nAI 캐릭터 포스터를 만들어보세요.',
      badge: '새학기 특별',
    },
    cta: { text: '무료로 만들어보기', dest: '/events/class-photo', sub: '첫 만남에 아이들이 환호하는 교실' },
  },

  // ── Group C: 긴급 할인형 (Scarcity -> 전환) ──

  'secret-deal': {
    adSetName: 'N_비밀링크가격',
    template: 'urgency-deal',
    hero: {
      headline: '이 링크를 본 분만',
      highlight: '30% 할인',
      sub: '시즌 한정 특별 가격이에요.\n이 페이지에서만 적용돼요.',
      badge: '비밀 링크 전용',
    },
    cta: { text: '지금 할인받고 시작하기', dest: '/subscription/pro?plan=12month&price=140000&promo=newyear2026', sub: '세금계산서 발급, 학교 예산 결제 가능해요' },
    urgencyText: '이 링크로만 적용되는 가격이에요',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'ppt',
  },

  'deadline': {
    adSetName: 'P_마감카운트다운',
    template: 'urgency-deal',
    hero: {
      headline: '시즌 특가',
      highlight: '최대 30% 할인 진행 중',
      sub: '지금 신청하면 최대 30% 할인을 받을 수 있어요.\n시즌 한정 특별 가격이에요.',
      badge: '시즌 특가',
    },
    cta: { text: '마감 전 신청하기', dest: '#form', sub: '월 11,667원으로 모든 교사 업무를 해결하세요' },
    hasForm: true,
    urgencyText: '시즌 특가 마감까지 카운트다운이 진행되고 있어요',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'saenggibu',
  },

  'daily-270': {
    adSetName: 'T_하루270원',
    template: 'urgency-deal',
    hero: {
      headline: '올해 가장 합리적인 투자',
      highlight: '하루 383원',
      sub: '커피 한 잔(4,500원)보다 싸게\n매일 칼퇴할 수 있어요.',
      badge: '하루 383원',
    },
    cta: { text: '하루 383원으로 신청하기', dest: '#form', sub: '12개월 140,000원, 정가 200,000원의 30% 할인이에요' },
    hasForm: true,
    urgencyText: '커피 한 잔 4,500원 vs 왓퀴즈 하루 383원',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'scoring',
  },

  'trial-deal': {
    adSetName: 'S_체험후할인',
    template: 'urgency-deal',
    hero: {
      headline: '7일 무료로 써보고',
      highlight: '시즌 특가로 시작하세요',
      sub: '카드 등록 없이 7일 완전 무료예요.\n마음에 안 들면 그냥 안 쓰면 돼요.',
      badge: '7일 무료 체험',
    },
    cta: { text: '무료 체험 시작하기', dest: '/scoring', sub: '카드 등록 없이 바로 체험, 체험 후 결제하면 50% 할인' },
    urgencyText: '7일 무료 체험 후 결제하면 50% 할인이 적용돼요',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'scoring',
  },

  'all-in-one-value': {
    adSetName: 'R_올인원가치',
    template: 'urgency-deal',
    hero: {
      headline: '교사 업무 도구',
      highlight: '5개를 하나로',
      sub: 'PPT 3분, 학습지 3분, 생기부 3분, 채점 2분, 이미지 1분\n지금이 가장 싼 가격이에요.',
      badge: '올인원 최저가',
    },
    cta: { text: '지금 최저가로 시작하기', dest: '/pricing', sub: '구글 계정으로 10초 가입, 바로 결제할 수 있어요' },
    urgencyText: '5개 도구를 하나의 가격으로 쓸 수 있어요',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'ppt',
  },

  'pre-register-deal': {
    adSetName: 'E_사전예약캐러셀',
    template: 'urgency-deal',
    hero: {
      headline: '교사 업무 도구',
      highlight: '하나로 끝내세요',
      sub: 'PPT, 학습지, 이미지, 생기부, 채점, 상담까지\n시즌 한정 최대 30% 할인이에요.',
      badge: '시즌 특가 30%',
    },
    cta: { text: '시즌 특가 신청하기', dest: '#form', sub: '시즌 마감, 세금계산서 발급 가능해요' },
    hasForm: true,
    urgencyText: '시즌 한정 최대 30% 할인 진행 중',
    showPrice: true,
    seasonAngle: 'default',
    demo: 'scoring',
  },

  // ── 시즌 한정 ad sets (생기부/수행평가/학교 행사) ──

  'record-deal': {
    adSetName: 'HR_생기부시즌',
    template: 'urgency-deal',
    hero: {
      headline: '30명 생기부,',
      highlight: '13시간 → 3분',
      sub: '학기말 생기부 마감 전, 미리 준비하세요.\nAI가 30명을 한번에 작성해드려요.',
      badge: '생기부 시즌',
    },
    cta: { text: '생기부 미리 시작하기', dest: '#form', sub: '미리 시작하면 학기말에 칼퇴할 수 있어요' },
    hasForm: true,
    urgencyText: '학기말 생기부 마감 전, 미리 준비하세요',
    showPrice: true,
    seasonAngle: 'record',
    demo: 'saenggibu',
  },

  'assessment-deal': {
    adSetName: 'HK_수행평가시즌',
    template: 'urgency-deal',
    hero: {
      headline: '수행평가 + 누가기록,',
      highlight: '한 번에 끝내요',
      sub: '수행평가 1·2차, 누가기록까지\nAI가 3분에 정리해드려요.',
      badge: '수행평가 시즌',
    },
    cta: { text: '수행평가 시즌 특가로 시작하기', dest: '#form', sub: '세금계산서 발급 가능해요' },
    hasForm: true,
    urgencyText: '수행평가 + 누가기록, AI가 도와드려요',
    showPrice: true,
    seasonAngle: 'assessment',
    demo: 'saenggibu',
  },

  'gajeong-deal': {
    adSetName: 'HG_가정의달',
    template: 'urgency-deal',
    hero: {
      headline: '학교 행사 시즌,',
      highlight: '몰리는 모든 업무',
      sub: '학교 행사 + 수행평가 + 생기부 누가기록\n12분 안에 정리해드려요.',
      badge: '행사 시즌',
    },
    cta: { text: '시즌 특별가로 시작하기', dest: '#form', sub: '시즌 한정 최대 30% 할인, 세금계산서 발급 가능' },
    hasForm: true,
    urgencyText: '행사 시즌에 몰리는 업무, 12분 안에 정리하세요',
    showPrice: true,
    seasonAngle: 'gajeong',
    demo: 'ppt',
  },

  // ── Group D: 감성/신뢰형 (Social Proof -> 신뢰) ──

  'early-leave': {
    adSetName: 'V_동료칼퇴',
    template: 'emotional',
    hero: {
      headline: '옆반 선생님은',
      highlight: '오늘도 칼퇴했습니다',
      sub: `${TEACHER_COUNT_LABEL}의 선생님이 몰래 쓰고 있는 도구예요.\n다음 칼퇴는 선생님 차례예요.`,
      badge: '칼퇴의 비결',
    },
    cta: { text: '나도 써보기', dest: '/chat?category=ppt_maker', sub: '가입 없이 바로 체험할 수 있어요' },
    story: {
      title: '왜 옆반 선생님만 일찍 가는지 궁금하셨죠?',
      quotes: [
        '생기부 시즌에 처음으로 야근 안 했어요',
        '동학년 선생님들 다 같이 쓰고 있어요',
        '부장님한테 추천받았어요',
      ],
      conclusion: '13시간 걸리던 업무를 12분으로 줄여드려요.\nPPT, 학습지, 생기부, 채점, 이미지까지.',
    },
    demo: 'ppt',
  },

  'honest': {
    adSetName: 'U_솔직고백',
    template: 'emotional',
    hero: {
      headline: '솔직히',
      highlight: '말씀드리겠습니다',
      sub: '작은 스타트업의 진심이에요.',
      badge: '솔직한 이야기',
    },
    cta: { text: '직접 써보고 판단하기', dest: '/create-saenggibu', sub: '가입 없이 바로 체험할 수 있어요' },
    story: {
      title: '왓퀴즈는 ChatGPT처럼 뭐든 다 하는 AI가 아니에요',
      quotes: [
        '대신, 교사 업무 딱 5가지만 미친 듯이 깎았어요.',
        '생기부 3분, PPT 3분, 학습지 3분, 채점 2분, 이미지 1분.',
        '대한민국 교사분들의 행정 업무를 12분 만에 끝내는 데는\n이 세상 어느 AI보다 가장 완벽하게 만들었어요.',
      ],
      conclusion: `KERIS 에듀테크 우수기업 인증\n현직 교사 ${TEACHER_COUNT_LABEL} 사용 중\n학교장터(S2B) 등록 완료`,
    },
    demo: 'saenggibu',
  },

  // ── Group E: 비교/FOMO형 (Anchoring -> 논리적 설득) ──

  'ai-compare': {
    adSetName: 'Y_AI비교표',
    template: 'comparison',
    hero: {
      headline: '3개 AI를 합쳐도 못 하는 걸',
      highlight: '왓퀴즈는 3분에 해요',
      sub: 'ChatGPT + Gemini + Claude = 월 84,000원\n왓퀴즈 = 7일 완전 무료 (카드 등록 없이)',
      badge: 'AI 비교',
    },
    cta: { text: '무료로 비교해보기', dest: '/chat', sub: '가입 없이 바로 체험할 수 있어요' },
    compare: {
      competitors: [
        { name: 'ChatGPT Pro', price: '월 29,000원' },
        { name: 'Gemini Advanced', price: '월 28,000원' },
        { name: 'Claude Pro', price: '월 27,000원' },
      ],
      whatquizPrice: '0원 (7일)',
      rows: [
        { label: '생기부 30명', values: [false, false, false], whatquiz: '3분' },
        { label: 'PPT 다운로드', values: [false, false, false], whatquiz: 'PPTX' },
        { label: 'AI 채점', values: [false, false, false], whatquiz: '사진 한 장' },
        { label: '교사 맞춤', values: [false, false, false], whatquiz: TEACHER_COUNT_LABEL },
      ],
    },
    demo: 'ppt',
  },

  'kakao-urgent': {
    adSetName: 'X_카톡긴급',
    template: 'comparison',
    hero: {
      headline: '교사 단톡방에서',
      highlight: '난리난 서비스',
      sub: 'GPT, 클로드, 제미나이를 한 곳에서 무제한으로 쓸 수 있어요.\n쿠폰코드 SPRING14를 넣으면 14일 무료예요.',
      badge: '단톡방 화제',
    },
    cta: { text: '14일 무료 생기부 체험하기', dest: '/create-saenggibu', sub: '가입 없이 바로 체험할 수 있어요' },
    couponCode: 'SPRING14',
    compare: {
      competitors: [
        { name: 'ChatGPT Pro', price: '월 29,000원' },
        { name: 'Gemini Advanced', price: '월 28,000원' },
        { name: 'Claude Pro', price: '월 27,000원' },
      ],
      whatquizPrice: '14일 무료',
      rows: [
        { label: '생기부 30명', values: [false, false, false], whatquiz: '3분' },
        { label: 'PPT 다운로드', values: [false, false, false], whatquiz: 'PPTX' },
        { label: 'AI 채점', values: [false, false, false], whatquiz: '사진 한 장' },
        { label: '교사 맞춤', values: [false, false, false], whatquiz: TEACHER_COUNT_LABEL },
      ],
    },
    demo: 'saenggibu',
  },

  'time-calculator': {
    adSetName: 'W_시간계산기',
    template: 'comparison',
    hero: {
      headline: '선생님의 시간을',
      highlight: '돌려드려요',
      sub: '지금까지 생기부에 쓴 시간: 약 390시간/년\n왓퀴즈 사용 시: 약 5시간/년',
      badge: '시간 절약 계산기',
    },
    cta: { text: '16일 돌려받기', dest: '#form', sub: '하루 383원이면 돼요' },
    hasForm: true,
    timeCalc: {
      before: { hours: 390, label: '지금까지 생기부에 쓴 시간/년' },
      after: { hours: 5, label: '왓퀴즈 사용 시/년' },
      saved: { hours: 385, days: 16, label: '돌려받는 시간' },
    },
    demo: 'saenggibu',
  },
};

export { COMMON_SOCIAL_PROOF, COMMON_FEATURES, PRICE_PLANS };
export default AD_LANDING_CONFIG;
